/**
 * AI Intake Assist — Create Ticket form-fill modal.
 *
 * V-OCIS-Intake-CreateTicket-Batch-v1. Differs from /ai-intake-lab:
 * this is the advisor-facing flow that prefills the existing Create
 * Ticket form rather than minting a ticket via approve-intake. The
 * lab page remains the internal diagnostic surface and is unchanged.
 *
 * Flow per tab:
 *   Paste Transcript:
 *     1. intake-create  (transcript_text → transcript_id)
 *     2. extract-intake (transcript_id  → ExtractIntakeResponse)
 *     3. advisor clicks Apply → parent receives the extraction
 *
 *   Record Audio (one-click stop-and-process):
 *     1. Advisor clicks Start Recording.
 *     2. Advisor attests consent (any time before stop).
 *     3. Advisor clicks Stop & Process Intake.
 *     4. Dialog auto-runs:
 *        a. MediaRecorder.stop()
 *        b. transcribe-intake-audio (with store_audio + consent)
 *        c. intake-create
 *        d. extract-intake
 *        e. onApply(extraction, transcriptId, intakeDraftId, pendingRecording)
 *        f. close dialog — advisor reviews & submits the Create Ticket form.
 *
 * The dialog never calls approve-intake or intake-attach-jobs from
 * this surface — those remain owned by the lab / future intake routes.
 * Ticket creation / scheduling / OptiPlace / Catalyst auto-assign all
 * stay behind the canonical Create Ticket submit; the dialog only
 * prefills the form.
 */

import { useEffect, useRef, useState } from 'react';
import { Loader2, Mic, Square, Sparkles, ClipboardPaste, AlertTriangle, Copy, FileAudio } from 'lucide-react';

import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  canAutoPrefillFromExtraction,
  getAiIntakeConfidenceWarning,
  getAiIntakeConfidenceLabel,
  getAiIntakePrefillMode,
} from '@/lib/aiIntake/prefillConfidence';

// ---------------------------------------------------------------
// Backend response types — mirror the edge function contracts.
// Kept independent of AiIntakeLab.tsx so the lab can evolve freely.
// ---------------------------------------------------------------

export interface ExtractedConcern {
  concern_text?: string;
  symptoms?: string[];
  when_it_happens?: string | null;
}

export interface ExtractedRequestedService {
  name?: string;
  category?: string | null;
  urgency?: string | null;
}

export interface ExtractedSuggestedJob {
  name?: string;
  category?: string | null;
  estimated_minutes?: number | null;
  skill_level?: string | null;
  rationale?: string;
  code?: string | null;
  catalog_label?: string | null;
  category_key?: string | null;
  sub_menu?: string | null;
  match_confidence?: 'high' | 'medium' | 'low' | 'none' | string | null;
  match_method?: string | null;
}

export interface ExtractedIntake {
  channel?: string;
  is_waiter?: boolean | null;
  is_drop_off?: boolean | null;
  promised_time?: string | null;
  urgency?: string | null;
  advisor_notes?: string | null;
}

export interface Extraction {
  schema_version: string;
  confidence: number;
  summary: string;
  customer: {
    full_name: string | null;
    phone: string | null;
    email: string | null;
    is_existing_match_hint?: string | null;
  };
  vehicle: {
    year: number | null;
    make: string | null;
    model: string | null;
    trim?: string | null;
    vin: string | null;
    license_plate: string | null;
    mileage: number | null;
  };
  intake: ExtractedIntake;
  concerns: ExtractedConcern[];
  requested_services: ExtractedRequestedService[];
  suggested_jobs: ExtractedSuggestedJob[];
  risk_flags: string[];
  missing_info: string[];
}

interface IntakeCreateResponse {
  ok: true;
  transcript_id: string;
}

interface ExtractIntakeResponse {
  ok: true;
  intake_draft_id: string;
  extraction: Extraction;
}

interface TranscribeAudioResponse {
  ok: true;
  transcript_text: string;
  language?: string | null;
  duration_ms?: number;
  provider?: string;
  model?: string;
  // Present only when the caller asked for store_audio + consent_attested.
  recording_artifact_id?: string;
  storage_bucket?: string;
  storage_path?: string;
  mime_type?: string;
  consent_attested_at?: string;
}

const MAX_AUDIO_BYTES = 25 * 1024 * 1024; // mirror server cap

// Internal AI Operations Guardrails Batch v1 — soft recording-duration
// guard. These are advisor-facing only (no provider-cost framing).
// Auto-stop prevents accidental hours-long recordings; the advisor can
// always start a fresh recording or paste a transcript instead.
const RECORDING_WARN_MS = 10 * 60 * 1000;  // 10 min — surface a warning
const RECORDING_AUTO_STOP_MS = 15 * 60 * 1000; // 15 min — auto-stop

// Retry-storm guard: after this many failed transcription attempts on
// the SAME recorded blob, surface a confirm checkbox before the next
// retry. Never permanently blocks — the advisor can confirm + retry
// indefinitely, or discard / paste-transcript instead.
const TRANSCRIBE_RETRY_CONFIRM_THRESHOLD = 2;

function formatElapsed(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

// ---------------------------------------------------------------
// Component contract
// ---------------------------------------------------------------

/**
 * Metadata for a raw recording uploaded to the `intake-recordings`
 * bucket and persisted as an `artifacts` row. Carried back to the
 * CreateTicket parent so it can attach the artifact to the ticket
 * after the canonical create flow completes.
 *
 * `transcriptId` (intake_transcripts.id) and `intakeDraftId` are
 * populated by the paste-flow's intake-create / extract-intake hops
 * after the advisor confirms the transcript — they are NOT known at
 * upload time. The CreateTicket consumer should use whichever values
 * are present.
 */
export interface AiIntakePendingRecording {
  recordingArtifactId: string;
  storageBucket: string;
  storagePath: string;
  mimeType: string;
  durationMs: number | null;
  byteSize: number | null;
  provider: string | null;
  model: string | null;
  consentMethod: string;
  consentAttestedAt: string | null;
}

export interface AiIntakeAssistApplyPayload {
  extraction: Extraction;
  transcriptId: string;
  intakeDraftId: string;
  /**
   * Set when the recording was uploaded with consent during this dialog
   * session. CreateTicket should attach it to the new ticket via
   * `attach-intake-recording-to-ticket` after the canonical submit.
   */
  pendingRecording?: AiIntakePendingRecording | null;
}

interface AiIntakeAssistDialogProps {
  isOpen: boolean;
  onClose: () => void;
  shopId: string | null;
  onApply: (payload: AiIntakeAssistApplyPayload) => void;
}

// ---------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------

async function invokeFn<T extends { ok: true }>(
  name: string,
  body: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    const message =
      (data as { error?: string; message?: string } | null)?.error ||
      (data as { error?: string; message?: string } | null)?.message ||
      error.message ||
      `${name} failed (network/auth error)`;
     
    console.error(`[ai-intake-assist] ${name} error`, error, data);
    throw new Error(message);
  }
  if (!data) {
    throw new Error(`${name} returned no data`);
  }
  const payload = data as { ok?: boolean; error?: string };
  if (payload.ok === false) {
    throw new Error(payload.error ?? `${name} returned ok:false`);
  }
  return data as T;
}

function confidenceBadgeClass(confidence: number): string {
  if (confidence >= 0.8) return 'bg-emerald-600 hover:bg-emerald-600';
  if (confidence >= 0.5) return 'bg-amber-600 hover:bg-amber-600';
  return 'bg-red-600 hover:bg-red-600';
}

function formatVehicleLine(v: Extraction['vehicle']): string {
  const parts = [
    v.year ? String(v.year) : null,
    v.make,
    v.model,
  ].filter((s) => s && String(s).trim().length > 0);
  return parts.join(' ');
}

// ---------------------------------------------------------------
// Component
// ---------------------------------------------------------------

export function AiIntakeAssistDialog({
  isOpen,
  onClose,
  shopId,
  onApply,
}: AiIntakeAssistDialogProps) {
  // Demo UX: default to Record Audio so the advisor lands on the
  // one-click flow. Paste Transcript remains available as a manual
  // fallback tab.
  const [tab, setTab] = useState<'paste' | 'record'>('record');

  // Paste tab state
  const [transcriptText, setTranscriptText] = useState('');
  const [transcriptId, setTranscriptId] = useState<string | null>(null);
  const [intakeDraftId, setIntakeDraftId] = useState<string | null>(null);
  const [extraction, setExtraction] = useState<Extraction | null>(null);
  const [working, setWorking] = useState(false);
  const [stepLabel, setStepLabel] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Record tab state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState<string | null>(null);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [transcribeError, setTranscribeError] = useState<string | null>(null);
  const [consentAttested, setConsentAttested] = useState(false);
  const [pendingRecording, setPendingRecording] = useState<AiIntakePendingRecording | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);

  // Internal AI Operations Guardrails Batch v1 — soft guardrails.
  // recordingElapsedMs is wall-clock since recording started; reset on
  // each new recording. transcribeRetryCount increments on each failed
  // transcribe of the same recorded blob and resets when a new blob is
  // recorded or the recording is discarded.
  const [recordingElapsedMs, setRecordingElapsedMs] = useState(0);
  const [autoStoppedNotice, setAutoStoppedNotice] = useState<string | null>(null);
  const [transcribeRetryCount, setTranscribeRetryCount] = useState(0);
  const [retryConfirmed, setRetryConfirmed] = useState(false);
  const recordingStartedAtRef = useRef<number | null>(null);
  const recordingTickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // When set, MediaRecorder.onstop kicks the captured blob into the
  // automatic transcribe → intake-create → extract-intake → onApply
  // pipeline. The fn ref always points at the latest closure-bound
  // copy of runAutomaticRecordingIntake so it reads fresh state.
  const autoProcessRequestedRef = useRef(false);
  const autoProcessFnRef = useRef<((blob: Blob) => void | Promise<void>) | null>(null);

  // Reset all state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      stopRecordingHard();
      setTab('record');
      setTranscriptText('');
      setTranscriptId(null);
      setIntakeDraftId(null);
      setExtraction(null);
      setWorking(false);
      setStepLabel('');
      setErrorMessage(null);
      setIsRecording(false);
      setRecordingError(null);
      setTranscribeError(null);
      setRecordedBlob(null);
      setConsentAttested(false);
      setPendingRecording(null);
      setRecordingElapsedMs(0);
      setAutoStoppedNotice(null);
      setTranscribeRetryCount(0);
      setRetryConfirmed(false);
      if (recordedAudioUrl) {
        URL.revokeObjectURL(recordedAudioUrl);
      }
      setRecordedAudioUrl(null);
    }
    // We intentionally do NOT depend on recordedAudioUrl — only run on open/close transitions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Stop tracks on unmount as a final safety so an in-flight stream
  // never outlives the modal (e.g. user navigates away mid-record).
  useEffect(() => {
    return () => stopRecordingHard();
     
  }, []);

  function stopRecordingHard() {
    try {
      mediaRecorderRef.current?.stop();
    } catch {
      /* noop */
    }
    audioStreamRef.current?.getTracks().forEach((t) => t.stop());
    audioStreamRef.current = null;
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
    if (recordingTickRef.current) {
      clearInterval(recordingTickRef.current);
      recordingTickRef.current = null;
    }
    recordingStartedAtRef.current = null;
  }

  async function handleStartRecording() {
    setRecordingError(null);
    setAutoStoppedNotice(null);
    setRecordingElapsedMs(0);
    // Reset retry-storm guard so a fresh recording starts with a clean
    // slate (it's tied to the previous blob, not this new one).
    setTranscribeRetryCount(0);
    setRetryConfirmed(false);
    if (!navigator?.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setRecordingError('This browser does not support audio recording.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      audioChunksRef.current = [];
      recorder.ondataavailable = (evt) => {
        if (evt.data && evt.data.size > 0) audioChunksRef.current.push(evt.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        if (recordedAudioUrl) URL.revokeObjectURL(recordedAudioUrl);
        const url = URL.createObjectURL(blob);
        setRecordedAudioUrl(url);
        setRecordedBlob(blob);
        audioStreamRef.current?.getTracks().forEach((t) => t.stop());
        audioStreamRef.current = null;
        // If the advisor clicked Stop & Process Intake, hand the blob
        // straight to the auto pipeline. The fn ref is kept fresh on
        // every render so it sees the latest state (consent, shopId).
        if (autoProcessRequestedRef.current) {
          autoProcessRequestedRef.current = false;
          const fn = autoProcessFnRef.current;
          if (fn) void fn(blob);
        }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);

      // Soft duration guard. Tick once a second so the warning surface
      // and the auto-stop fire close to their advertised thresholds.
      // The auto-stop is operationally useful — it prevents accidental
      // hours-long recordings — and is non-blocking: the advisor can
      // start a new recording or paste a transcript.
      recordingStartedAtRef.current = Date.now();
      if (recordingTickRef.current) clearInterval(recordingTickRef.current);
      recordingTickRef.current = setInterval(() => {
        const startedAt = recordingStartedAtRef.current;
        if (startedAt == null) return;
        const elapsed = Date.now() - startedAt;
        setRecordingElapsedMs(elapsed);
        if (elapsed >= RECORDING_AUTO_STOP_MS) {
          setAutoStoppedNotice(
            `Recording auto-stopped at ${Math.floor(RECORDING_AUTO_STOP_MS / 60000)} minutes. Start a new recording or paste a transcript.`,
          );
          // handleStopRecording also clears the interval via stopRecordingHard
          // through MediaRecorder.onstop semantics; here we just trigger stop.
          handleStopRecording();
        }
      }, 1000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setRecordingError(`Mic permission failed: ${msg}`);
    }
  }

  function handleStopRecording() {
    try {
      mediaRecorderRef.current?.stop();
    } catch {
      /* noop */
    }
    setIsRecording(false);
    if (recordingTickRef.current) {
      clearInterval(recordingTickRef.current);
      recordingTickRef.current = null;
    }
    recordingStartedAtRef.current = null;
  }

  /**
   * One-click Stop & Process Intake. Flips the auto-process ref and
   * stops the MediaRecorder — the recorder.onstop handler then hands
   * the captured blob to runAutomaticRecordingIntake.
   *
   * Consent is required and validated again inside the auto pipeline
   * (defence-in-depth — the button is also disabled until attested).
   */
  function handleStopAndProcess() {
    if (!consentAttested) {
      toast({
        title: 'Consent required',
        description: 'Confirm the customer consented before processing.',
        variant: 'destructive',
      });
      return;
    }
    if (!shopId) {
      toast({
        title: 'No active shop',
        description: 'Select a shop before processing intake.',
        variant: 'destructive',
      });
      return;
    }
    autoProcessRequestedRef.current = true;
    handleStopRecording();
  }

  /**
   * Demo UX: checking the consent box also auto-starts the recording
   * (so the advisor doesn't have to click Start Recording separately).
   * Guarded against duplicate starts:
   *   - only fires when consent transitions to true
   *   - only fires when idle (!isRecording && !recordedBlob && !working)
   * If the microphone permission fails, the existing recordingError
   * surface stays — the user can retry via the fallback Start Recording
   * button (which appears once consent is already attested).
   *
   * Unchecking is locked while recording (the checkbox is disabled
   * during isRecording), so we never have to reconcile a mid-call
   * uncheck.
   */
  function handleConsentChange(checked: boolean | 'indeterminate') {
    const next = checked === true;
    setConsentAttested(next);
    if (next && !isRecording && !recordedBlob && !working) {
      void handleStartRecording();
    }
  }

  function handleDiscardRecording() {
    if (recordedAudioUrl) URL.revokeObjectURL(recordedAudioUrl);
    setRecordedAudioUrl(null);
    setRecordedBlob(null);
    setTranscribeError(null);
    setPendingRecording(null);
    setConsentAttested(false);
    setRecordingElapsedMs(0);
    setAutoStoppedNotice(null);
    setTranscribeRetryCount(0);
    setRetryConfirmed(false);
  }

  /**
   * One-click stop-and-process pipeline. Reuses the same edge function
   * contracts as the paste flow, just chained without intermediate UI:
   *
   *   1. transcribe-intake-audio (with store_audio + consent attestation)
   *   2. intake-create
   *   3. extract-intake
   *   4. onApply(...) — parent prefills the Create Ticket form
   *   5. onClose() — advisor reviews and submits normally
   *
   * Boundary: this never calls approve-intake or intake-attach-jobs and
   * never creates / schedules / assigns tickets. It only fills the
   * Create Ticket form; the canonical Create Ticket submit path remains
   * authoritative.
   *
   * On transcription failure: the recorded blob + URL are preserved so
   * the advisor can retry or discard. On intake-create / extract-intake
   * failure: the transcript text is preserved in the Paste tab so the
   * advisor can manually re-run Extract Intake.
   */
  async function runAutomaticRecordingIntake(blob: Blob): Promise<void> {
    if (!blob) return;
    if (!shopId) {
      toast({
        title: 'No active shop',
        description: 'Select a shop before processing intake.',
        variant: 'destructive',
      });
      return;
    }
    if (!consentAttested) {
      toast({
        title: 'Consent required',
        description: 'Confirm the customer consented before processing.',
        variant: 'destructive',
      });
      return;
    }
    if (blob.size > MAX_AUDIO_BYTES) {
      setTranscribeError(
        `Recording is too large (${(blob.size / 1024 / 1024).toFixed(1)} MB; max 25 MB).`,
      );
      return;
    }

    setWorking(true);
    setTranscribeError(null);
    setErrorMessage(null);

    let transcript = '';
    let recordingMeta: AiIntakePendingRecording | null = null;

    // ---- Step 1: Transcribe ----
    try {
      setStepLabel('Transcribing…');
      const form = new FormData();
      form.append('shop_id', shopId);
      const filename = `intake-audio.${(blob.type.split('/')[1] || 'webm').split(';')[0]}`;
      form.append('audio', blob, filename);
      // V-OCIS-Intake-RawAudio-Batch-v1: opt-in storage + consent.
      form.append('store_audio', 'true');
      form.append('consent_attested', 'true');
      form.append('consent_method', 'advisor_attestation');

      const { data, error } = await supabase.functions.invoke('transcribe-intake-audio', {
        body: form,
      });
      if (error) {
        const msg =
          (data as { error?: string } | null)?.error || error.message || 'Transcription failed';
        throw new Error(msg);
      }
      if (!data) throw new Error('transcribe-intake-audio returned no data');
      const payload = data as { ok?: boolean; error?: string } & TranscribeAudioResponse;
      if ((payload as { ok?: boolean }).ok === false) {
        throw new Error(payload.error ?? 'Transcription failed');
      }

      transcript = (payload.transcript_text ?? '').trim();
      if (!transcript) {
        throw new Error('Transcription returned empty text — try recording again.');
      }

      if (payload.recording_artifact_id && payload.storage_path && payload.storage_bucket) {
        recordingMeta = {
          recordingArtifactId: payload.recording_artifact_id,
          storageBucket: payload.storage_bucket,
          storagePath: payload.storage_path,
          mimeType: payload.mime_type ?? blob.type ?? 'audio/webm',
          durationMs: payload.duration_ms ?? null,
          byteSize: blob.size,
          provider: payload.provider ?? 'groq_whisper',
          model: payload.model ?? 'whisper-large-v3',
          consentMethod: 'advisor_attestation',
          consentAttestedAt: payload.consent_attested_at ?? null,
        };
        setPendingRecording(recordingMeta);
      }

      setTranscriptText(transcript);
      setTranscribeRetryCount(0);
      setRetryConfirmed(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setTranscribeError(msg);
      setTranscribeRetryCount((n) => n + 1);
      setRetryConfirmed(false);
      setStepLabel('');
      setWorking(false);
      toast({
        title: 'Transcription failed',
        description: msg,
        variant: 'destructive',
      });
      return;
    }

    // ---- Step 2 + 3: intake-create then extract-intake ----
    let createdTranscriptId: string | null = null;
    let createdDraftId: string | null = null;
    let createdExtraction: Extraction | null = null;
    try {
      setStepLabel('Creating intake transcript…');
      const created = await invokeFn<IntakeCreateResponse>('intake-create', {
        shop_id: shopId,
        transcript_text: transcript,
      });
      createdTranscriptId = created.transcript_id;
      setTranscriptId(created.transcript_id);

      setStepLabel('Extracting intake…');
      const result = await invokeFn<ExtractIntakeResponse>('extract-intake', {
        shop_id: shopId,
        transcript_id: created.transcript_id,
      });
      createdDraftId = result.intake_draft_id;
      createdExtraction = result.extraction;
      setIntakeDraftId(result.intake_draft_id);
      setExtraction(result.extraction);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrorMessage(msg);
      setStepLabel('');
      setWorking(false);
      // Preserve transcript in the paste tab so the advisor can manually
      // re-run Extract Intake without re-recording.
      setTab('paste');
      toast({
        title: 'AI intake failed',
        description: msg,
        variant: 'destructive',
      });
      return;
    }

    // ---- Step 4: Apply to Create Ticket form ----
    // AII-3: only AUTO-prefill on HIGH overall extraction confidence. For
    // medium / low / unknown confidence, do NOT silently populate
    // authoritative ticket fields — leave the extraction displayed for
    // deliberate advisor review + explicit Apply. The extracted content is
    // preserved either way (fail-safe; never fail-open).
    if (
      createdExtraction &&
      createdTranscriptId &&
      createdDraftId &&
      canAutoPrefillFromExtraction(createdExtraction.confidence)
    ) {
      setStepLabel('Applying to ticket form…');
      onApply({
        extraction: createdExtraction,
        transcriptId: createdTranscriptId,
        intakeDraftId: createdDraftId,
        pendingRecording: recordingMeta,
      });
      setStepLabel('');
      setWorking(false);
      onClose();
      return;
    }

    // Not high-confidence (or missing ids): stop for advisor review. Keep the
    // dialog open with the extraction visible; do not auto-apply or close.
    setStepLabel('');
    setWorking(false);
    if (createdExtraction) {
      setTab('paste');
      toast({
        title: 'Review required before applying',
        description:
          getAiIntakeConfidenceWarning(createdExtraction.confidence) ??
          'Review the AI-extracted fields, then click Apply to Ticket Form.',
      });
    }
  }

  // Keep the fn ref pointing at the latest closure so MediaRecorder.onstop
  // (created in handleStartRecording's closure) can call into the most
  // recently rendered version with fresh state.
  autoProcessFnRef.current = runAutomaticRecordingIntake;

  async function handleExtract() {
    const text = transcriptText.trim();
    if (!text) {
      toast({
        title: 'Transcript required',
        description: 'Paste or type the intake conversation first.',
        variant: 'destructive',
      });
      return;
    }
    if (!shopId) {
      toast({
        title: 'No active shop',
        description: 'Select a shop before running AI intake.',
        variant: 'destructive',
      });
      return;
    }
    setWorking(true);
    setErrorMessage(null);
    try {
      setStepLabel('Creating transcript…');
      const created = await invokeFn<IntakeCreateResponse>('intake-create', {
        shop_id: shopId,
        transcript_text: text,
      });
      setTranscriptId(created.transcript_id);

      setStepLabel('Extracting with AI…');
      const result = await invokeFn<ExtractIntakeResponse>('extract-intake', {
        shop_id: shopId,
        transcript_id: created.transcript_id,
      });
      setIntakeDraftId(result.intake_draft_id);
      setExtraction(result.extraction);
      setStepLabel('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrorMessage(msg);
      toast({
        title: 'AI intake failed',
        description: msg,
        variant: 'destructive',
      });
    } finally {
      setWorking(false);
    }
  }

  function handleApplyToForm() {
    if (!extraction || !transcriptId || !intakeDraftId) return;
    onApply({
      extraction,
      transcriptId,
      intakeDraftId,
      pendingRecording: pendingRecording ?? null,
    });
    // Parent decides whether to close; we close by default for clarity.
    onClose();
  }

  async function handleCopyAdvisorNotes() {
    const notes = extraction?.intake?.advisor_notes?.trim();
    if (!notes) return;
    try {
      await navigator.clipboard.writeText(notes);
      toast({ title: 'Copied advisor notes' });
    } catch (err) {
      toast({
        title: 'Copy failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            AI Intake Assist
          </DialogTitle>
          <DialogDescription>
            Paste an intake conversation or record audio, then let AI prefill
            the Create Ticket form. You review and submit normally.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={(v) => setTab(v as 'paste' | 'record')} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="paste" data-testid="ai-intake-tab-paste">
              <ClipboardPaste className="mr-2 h-4 w-4" /> Paste Transcript
            </TabsTrigger>
            <TabsTrigger value="record" data-testid="ai-intake-tab-record">
              <Mic className="mr-2 h-4 w-4" /> Record Audio
            </TabsTrigger>
          </TabsList>

          {/* ------- PASTE TAB ------- */}
          <TabsContent value="paste" className="space-y-4 pt-4">
            <Textarea
              value={transcriptText}
              onChange={(e) => setTranscriptText(e.target.value)}
              placeholder="Customer: Hi, my 2019 Civic is making a rattling noise above 40 mph..."
              rows={8}
              disabled={working || !!extraction}
              data-testid="ai-intake-transcript-input"
            />

            <div className="flex items-center gap-3">
              <Button
                onClick={handleExtract}
                disabled={working || !!extraction || !transcriptText.trim()}
                data-testid="ai-intake-extract-button"
              >
                {working && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Extract Intake
              </Button>
              {extraction && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setExtraction(null);
                    setTranscriptId(null);
                    setIntakeDraftId(null);
                    setErrorMessage(null);
                  }}
                  disabled={working}
                >
                  Start Over
                </Button>
              )}
              {working && stepLabel && (
                <span className="text-sm text-muted-foreground">{stepLabel}</span>
              )}
            </div>

            {errorMessage && !working && (
              <Alert variant="destructive">
                <AlertTitle>AI intake failed</AlertTitle>
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            )}

            {extraction && (
              <div className="space-y-4 rounded border p-4" data-testid="ai-intake-preview">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <h3 className="font-semibold">AI Extraction Preview</h3>
                  <div className="flex items-center gap-2">
                    <Badge className={confidenceBadgeClass(extraction.confidence)}>
                      Confidence: {getAiIntakeConfidenceLabel(extraction.confidence)} ({extraction.confidence.toFixed(2)})
                    </Badge>
                  </div>
                </div>

                {/* AII-3: non-high confidence requires deliberate advisor
                    review before applying. Wording never implies the AI is
                    verified; the extracted content remains visible below. */}
                {getAiIntakeConfidenceWarning(extraction.confidence) && (
                  <Alert variant="destructive" data-testid="ai-intake-confidence-warning">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>
                      {getAiIntakePrefillMode(extraction.confidence) === 'review'
                        ? 'Review before submitting'
                        : 'Review required before applying'}
                    </AlertTitle>
                    <AlertDescription>
                      {getAiIntakeConfidenceWarning(extraction.confidence)}
                    </AlertDescription>
                  </Alert>
                )}

                {extraction.summary && (
                  <section>
                    <h4 className="text-sm font-semibold">Summary</h4>
                    <p className="text-sm text-muted-foreground">{extraction.summary}</p>
                  </section>
                )}

                <div className="grid gap-4 md:grid-cols-2">
                  <section>
                    <h4 className="text-sm font-semibold mb-1">Customer</h4>
                    <dl className="text-sm space-y-0.5">
                      <div><span className="text-muted-foreground">Name: </span>{extraction.customer?.full_name ?? <em>—</em>}</div>
                      <div><span className="text-muted-foreground">Phone: </span>{extraction.customer?.phone ?? <em>—</em>}</div>
                      <div><span className="text-muted-foreground">Email: </span>{extraction.customer?.email ?? <em>—</em>}</div>
                    </dl>
                  </section>
                  <section>
                    <h4 className="text-sm font-semibold mb-1">Vehicle</h4>
                    <dl className="text-sm space-y-0.5">
                      <div>{formatVehicleLine(extraction.vehicle) || <em>—</em>}</div>
                      <div><span className="text-muted-foreground">VIN: </span>{extraction.vehicle?.vin ?? <em>—</em>}</div>
                      <div><span className="text-muted-foreground">Plate: </span>{extraction.vehicle?.license_plate ?? <em>—</em>}</div>
                      <div><span className="text-muted-foreground">Mileage: </span>{extraction.vehicle?.mileage ?? <em>—</em>}</div>
                    </dl>
                  </section>
                </div>

                {extraction.concerns.length > 0 && (
                  <section>
                    <h4 className="text-sm font-semibold mb-1">Concerns</h4>
                    <ul className="space-y-1 text-sm">
                      {extraction.concerns.map((c, i) => (
                        <li key={i}>
                          <div className="font-medium">{c.concern_text}</div>
                          {c.when_it_happens && (
                            <div className="text-xs text-muted-foreground">When: {c.when_it_happens}</div>
                          )}
                          {c.symptoms && c.symptoms.length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {c.symptoms.map((s, j) => (
                                <Badge key={j} variant="secondary">{s}</Badge>
                              ))}
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {extraction.requested_services.length > 0 && (
                  <section>
                    <h4 className="text-sm font-semibold mb-1">Requested services</h4>
                    <ul className="list-disc pl-5 text-sm">
                      {extraction.requested_services.map((s, i) => (
                        <li key={i}>
                          {s.name}
                          {s.category && <span className="text-muted-foreground"> · {s.category}</span>}
                          {s.urgency && <span className="text-muted-foreground"> · {s.urgency}</span>}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {extraction.suggested_jobs.length > 0 && (
                  <section>
                    <h4 className="text-sm font-semibold mb-1">Suggested jobs</h4>
                    <p className="text-xs text-muted-foreground mb-1">
                      Applying will try to match these against the shop's job catalog.
                      Unmatched suggestions are listed in the ticket form for manual review.
                    </p>
                    <ul className="space-y-1 text-sm">
                      {extraction.suggested_jobs.map((j, i) => (
                        <li key={i} className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{j.name}</span>
                          {j.code && <Badge variant="outline">{j.code}</Badge>}
                          {j.estimated_minutes != null && (
                            <Badge variant="outline">{j.estimated_minutes} min</Badge>
                          )}
                          {j.category && <Badge variant="secondary">{j.category}</Badge>}
                          {j.match_confidence && j.match_confidence !== 'none' && (
                            <Badge variant="outline">match: {j.match_confidence}</Badge>
                          )}
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {extraction.intake?.advisor_notes && (
                  <section>
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold">Advisor notes</h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCopyAdvisorNotes}
                        type="button"
                      >
                        <Copy className="mr-1 h-3 w-3" /> Copy
                      </Button>
                    </div>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {extraction.intake.advisor_notes}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Create Ticket has no ticket-level notes field — paste these into
                      a specific job's notes if needed.
                    </p>
                  </section>
                )}

                {(extraction.missing_info.length > 0 || extraction.risk_flags.length > 0) && (
                  <section className="grid gap-3 md:grid-cols-2">
                    {extraction.missing_info.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold mb-1">Missing info</h4>
                        <div className="flex flex-wrap gap-1">
                          {extraction.missing_info.map((m, i) => (
                            <Badge key={i} variant="outline">{m}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {extraction.risk_flags.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold mb-1">Risk flags</h4>
                        <div className="flex flex-wrap gap-1">
                          {extraction.risk_flags.map((r, i) => (
                            <Badge key={i} variant="destructive">{r}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </section>
                )}
              </div>
            )}
          </TabsContent>

          {/* ------- RECORD TAB (one-click stop-and-process) ------- */}
          <TabsContent value="record" className="space-y-4 pt-4">
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>One-click intake recording</AlertTitle>
              <AlertDescription>
                With consent, the recording is securely saved, transcribed,
                prepared for attachment to the created ticket, and logged to
                OCIS after ticket creation. The advisor still reviews and
                submits the Create Ticket form — this dialog only prefills it.
              </AlertDescription>
            </Alert>

            {/* Consent attestation — always visible so the advisor can attest
                before, during, or after recording. Required to enable the
                Stop & Process Intake action. */}
            <div
              className="flex items-start gap-2 rounded border bg-muted/30 p-2"
              data-testid="ai-intake-consent-block"
            >
              <Checkbox
                id="ai-intake-consent"
                checked={consentAttested}
                onCheckedChange={handleConsentChange}
                // Lock consent once recording is in flight so the advisor
                // can't silently uncheck mid-call and leave the dialog in
                // a confusing state (option a: disable unchecking while
                // recording). Auto-start is gated by the same predicate
                // inside handleConsentChange.
                disabled={working || !!pendingRecording || isRecording}
                data-testid="ai-intake-consent-checkbox"
              />
              <div className="space-y-1 leading-tight">
                <Label htmlFor="ai-intake-consent" className="text-sm cursor-pointer">
                  Customer consented to this recording.
                </Label>
                <p className="text-xs text-muted-foreground">
                  Required before Stop &amp; Process Intake. You're attesting
                  on the customer's behalf — this is recorded as
                  <code className="mx-1">advisor_attestation</code>
                  against your user id.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {/* Fallback Start Recording: only visible when consent is
                  already attested but recording did not auto-start (e.g.
                  the auto-start on consent-check raced, or mic permission
                  failed and the advisor wants to retry). */}
              {consentAttested && !isRecording && !working && !recordedBlob && (
                <Button
                  onClick={handleStartRecording}
                  data-testid="ai-intake-start-recording"
                  type="button"
                >
                  <Mic className="mr-2 h-4 w-4" /> Start Recording
                </Button>
              )}
              {isRecording && (
                <>
                  <Button
                    variant="destructive"
                    onClick={handleStopAndProcess}
                    disabled={!consentAttested || !shopId}
                    data-testid="ai-intake-stop-and-process"
                    type="button"
                  >
                    <Square className="mr-2 h-4 w-4" /> Stop &amp; Process Intake
                  </Button>
                  <span
                    className="text-sm text-muted-foreground inline-flex items-center gap-2"
                    data-testid="ai-intake-recording-indicator"
                  >
                    <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                    Recording… {formatElapsed(recordingElapsedMs)}
                  </span>
                </>
              )}
              {working && stepLabel && (
                <span
                  className="text-sm text-muted-foreground inline-flex items-center gap-2"
                  data-testid="ai-intake-processing-status"
                >
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {stepLabel}
                </span>
              )}
            </div>

            {isRecording && !consentAttested && (
              <p className="text-xs text-muted-foreground" data-testid="ai-intake-consent-hint">
                Tick the consent box to enable Stop &amp; Process Intake.
              </p>
            )}

            {isRecording && recordingElapsedMs >= RECORDING_WARN_MS && (
              <Alert variant="default" data-testid="ai-intake-recording-warning">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Recording is getting long</AlertTitle>
                <AlertDescription>
                  Stop and restart if you only need the most recent part — long
                  recordings transcribe slower. Auto-stops at{' '}
                  {Math.floor(RECORDING_AUTO_STOP_MS / 60000)} minutes.
                </AlertDescription>
              </Alert>
            )}

            {autoStoppedNotice && !isRecording && (
              <Alert data-testid="ai-intake-recording-auto-stopped">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Recording stopped automatically</AlertTitle>
                <AlertDescription>{autoStoppedNotice}</AlertDescription>
              </Alert>
            )}

            {recordingError && (
              <Alert variant="destructive">
                <AlertTitle>Recording error</AlertTitle>
                <AlertDescription>{recordingError}</AlertDescription>
              </Alert>
            )}

            {/* Failure state: keep the recorded blob available with a
                retry-or-discard surface so a transient transcription
                failure doesn't lose the advisor's recording. */}
            {recordedBlob && !isRecording && !working && transcribeError && (
              <div className="space-y-3" data-testid="ai-intake-recording-failure">
                <audio src={recordedAudioUrl ?? undefined} controls className="w-full" />
                <p className="text-xs text-muted-foreground inline-flex items-center gap-1">
                  <FileAudio className="h-3 w-3" />
                  {(recordedBlob.size / 1024).toFixed(1)} KB ·
                  {recordedBlob.type || 'audio/webm'}
                </p>

                {transcribeRetryCount >= TRANSCRIBE_RETRY_CONFIRM_THRESHOLD &&
                  !pendingRecording && (
                    <div
                      className="flex items-start gap-2 rounded border border-amber-500/40 bg-amber-500/5 p-2"
                      data-testid="ai-intake-retry-confirm-block"
                    >
                      <Checkbox
                        id="ai-intake-retry-confirm"
                        checked={retryConfirmed}
                        onCheckedChange={(checked) => setRetryConfirmed(checked === true)}
                        data-testid="ai-intake-retry-confirm-checkbox"
                      />
                      <div className="space-y-1 leading-tight">
                        <Label htmlFor="ai-intake-retry-confirm" className="text-sm cursor-pointer">
                          Transcription failed {transcribeRetryCount} time
                          {transcribeRetryCount === 1 ? '' : 's'}. Retry anyway?
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          You can also switch to <strong>Paste Transcript</strong>
                          {' '}or discard this recording.
                        </p>
                      </div>
                    </div>
                  )}

                <div className="flex items-center gap-3 flex-wrap">
                  <Button
                    onClick={() => runAutomaticRecordingIntake(recordedBlob)}
                    disabled={
                      !recordedBlob ||
                      !shopId ||
                      !consentAttested ||
                      (transcribeRetryCount >= TRANSCRIBE_RETRY_CONFIRM_THRESHOLD &&
                        !retryConfirmed)
                    }
                    data-testid="ai-intake-retry-button"
                    type="button"
                  >
                    {transcribeRetryCount > 0 ? 'Retry Process Intake' : 'Process Intake'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDiscardRecording}
                    type="button"
                  >
                    Discard
                  </Button>
                </div>

                <Alert variant="destructive">
                  <AlertTitle>Transcription failed</AlertTitle>
                  <AlertDescription>
                    {transcribeError}
                    <div className="mt-2 text-xs">
                      Retry, switch to <strong>Paste Transcript</strong>, or
                      discard the recording.
                    </div>
                  </AlertDescription>
                </Alert>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button
            onClick={handleApplyToForm}
            disabled={!extraction || working}
            data-testid="ai-intake-apply-button"
            type="button"
          >
            <Sparkles className="mr-2 h-4 w-4" />
            {/* AII-3: explicit advisor apply is always allowed (deliberate
                action); the label signals review for non-high confidence. */}
            {extraction && !canAutoPrefillFromExtraction(extraction.confidence)
              ? 'Review & Apply to Ticket Form'
              : 'Apply to Ticket Form'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AiIntakeAssistDialog;
